import {
  getDestinationCampaignHotels,
  resolveCampaignContract,
} from "../data/destinationCampaignHotels";
import { resolveBelvedereRoomImageForOffer } from "../data/belvedereRoomImages";
import { resolveRoyalTulipRoomImageForOffer } from "../data/royalTulipTbilisiRoomImages";
import { belvedereContract } from "../data/belvedereContractData";
import { royalTulipTbilisiContract } from "../data/royalTulipTbilisiContractData";
import {
  getCompatibleRoomOffers,
  toSelectedRoom,
} from "../services/belvederePricing";
import { getCompatibleRoyalTulipRoomOffers } from "../services/royalTulipPricing";
import {
  applyInventoryGateToOffers,
  buildInventoryByOfferMap,
  enrichOffersWithInventory,
  fetchBelvedereCampaignAvailability,
  offerInventoryKey,
} from "../services/belvedereCampaignInventory";
import { isoDateFromDdMmYyyy } from "./agentRecentSearchPayload";
import { roomsFromHotelSearchPhrase } from "./injectDestinationCampaignHotels";
import { formatHotelSearchAmountForDisplay } from "../constants/hotelCurrencies";
import { roundMoney2 } from "../pricing/pricingEngine";
import {
  BELVEDERE_AGENT_HOTEL_MARKUP_PERCENT,
  formatHotelContractMoney,
  resolveBelvederePublicHotelEffectiveMarkupPercent,
} from "../services/belvederePricing";

export function isCampaignAllotmentPackage(pkg) {
  return Boolean(pkg && typeof pkg === "object" && pkg.isCampaignAllotment === true);
}

const CAMPAIGN_BOARD_LABEL_EN = {
  BB: "Breakfast",
  HB: "Half Board",
  FB: "Full Board",
  AI: "All Inclusive",
  RO: "Room Only",
};

const CAMPAIGN_BOARD_LABEL_EN_KOSHER = {
  BB: "Kosher Breakfast",
  HB: "Kosher Half Board",
};

function campaignBoardSourceRoom(source) {
  return source?.room && typeof source.room === "object" ? source.room : null;
}

function campaignBoardHeText(source) {
  if (!source || typeof source !== "object") return "";
  const room = campaignBoardSourceRoom(source);
  return String(
    source.boardHe ||
      source.boardBasisLabel ||
      room?.boardHe ||
      source.campaignOffer?.room?.boardHe ||
      source.campaignSelectedRoom?.boardHe ||
      "",
  ).trim();
}

function campaignBoardCode(source) {
  if (!source || typeof source !== "object") return "";
  const room = campaignBoardSourceRoom(source);
  const raw = String(
    source.roomBasis ||
      source.RoomBasis ||
      source.board ||
      room?.board ||
      source.campaignOffer?.room?.board ||
      source.campaignSelectedRoom?.board ||
      "",
  )
    .trim()
    .toUpperCase();
  if (!raw) return "";
  if (CAMPAIGN_BOARD_LABEL_EN[raw]) return raw;
  const token = raw.split(/[\s/_-]+/).filter(Boolean).pop();
  return CAMPAIGN_BOARD_LABEL_EN[token] ? token : raw;
}

function campaignBoardIsKosher(source) {
  if (!source || typeof source !== "object") return false;
  const room = campaignBoardSourceRoom(source);
  if (
    source.campaignKosher === true ||
    source.kosher === true ||
    room?.campaignKosher === true ||
    room?.kosher === true
  ) {
    return true;
  }
  return /כשר/.test(campaignBoardHeText(source));
}

export function campaignBoardDisplayLabel(pkg) {
  if (!pkg || typeof pkg !== "object") return null;
  if (!isCampaignAllotmentPackage(pkg) && !pkg?.campaignQuoteKind) return null;
  const he = campaignBoardHeText(pkg);
  if (he) return he;
  if (!pkg.campaignKosher) return null;
  const code = campaignBoardCode(pkg);
  if (code === "BB") return "ארוחת בוקר כשרה";
  if (code === "HB") return "חצי פנסיון כשר";
  return null;
}

/** English board line under the room type name. Does not replace Hebrew overlay labels. */
export function campaignBoardDisplayLabelEn(source) {
  if (!source || typeof source !== "object") return null;
  const code = campaignBoardCode(source);
  if (!code) return null;
  if (campaignBoardIsKosher(source) && CAMPAIGN_BOARD_LABEL_EN_KOSHER[code]) {
    return CAMPAIGN_BOARD_LABEL_EN_KOSHER[code];
  }
  return CAMPAIGN_BOARD_LABEL_EN[code] || null;
}

function campaignContractForPkg(pkg) {
  return (
    resolveCampaignContract(pkg?.campaignHotelCode || pkg?.campaignSlug) ||
    (String(pkg?.campaignQuoteKind || "") === "royalTulip"
      ? royalTulipTbilisiContract
      : belvedereContract)
  );
}

export function resolveCampaignHotelMarkupFactor(pkg) {
  const contract = campaignContractForPkg(pkg);
  if (pkg?.campaignPublicChannel) {
    return 1 + resolveBelvederePublicHotelEffectiveMarkupPercent(contract) / 100;
  }
  const pct = Number(contract?.agentMarkupPercent);
  const markup =
    Number.isFinite(pct) && pct > 0 ? pct : BELVEDERE_AGENT_HOTEL_MARKUP_PERCENT;
  return 1 + markup / 100;
}

export function normalizeCampaignContractCurrency(currency, fallback = "USD") {
  const code = String(currency || fallback).trim().toUpperCase();
  return code === "EUR" ? "EUR" : "USD";
}

export function campaignContractCurrencySymbol(currency, fallback = "USD") {
  return normalizeCampaignContractCurrency(currency, fallback) === "EUR" ? "€" : "$";
}

/** Inventory calendar / day drawer — contract net with the hotel's currency symbol. */
export function formatCampaignInventoryNetPrice(row, currency, { compact = false } = {}) {
  if (!row || row.net_price_eur == null) return null;
  const symbol = campaignContractCurrencySymbol(currency);
  if (compact) return `${symbol}${row.net_price_eur}`;
  const unit = row.charge_type === "per_unit" ? "יח׳" : "אורח";
  return `${symbol}${row.net_price_eur}/${unit}`;
}

/** Same label as Admin «שער נוכחי: X₪ = 1$». */
export function formatCampaignAdminFxRateLabel(rate, currency = "USD") {
  const n = Number(rate);
  if (!Number.isFinite(n) || n <= 0) return null;
  const code = normalizeCampaignContractCurrency(currency);
  if (code === "EUR") return `${n}₪ = 1€`;
  return `${n}₪ = 1$`;
}

export function resolveCampaignAdminFxRate(currency, profilePriceContext) {
  const code = normalizeCampaignContractCurrency(currency);
  if (code === "EUR") {
    const n = Number(
      profilePriceContext?.adminEurRate ??
        profilePriceContext?.EURrate ??
        profilePriceContext?.currencyRate?.EUR,
    );
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const n = Number(
    profilePriceContext?.adminUsdRate ??
      profilePriceContext?.USDrate ??
      profilePriceContext?.currencyRate?.USD,
  );
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function resolveDestinationCampaignForHotelProfile({
  hotelInfo,
  seed,
  searchPhrase,
} = {}) {
  const slug = String(
    seed?.campaignSlug || searchPhrase?.campaignSlug || "",
  ).trim();
  const hotels = getDestinationCampaignHotels();
  if (slug) {
    const bySlug = hotels.find((row) => row.slug === slug || row.hotelCode === slug);
    if (bySlug) return bySlug;
  }
  const gimmonixId =
    hotelInfo?.HotelId ??
    hotelInfo?.ID ??
    hotelInfo?.HotelID ??
    seed?.ID ??
    seed?.HotelId ??
    seed?.gimmonixHotelId;
  if (gimmonixId != null && String(gimmonixId).trim()) {
    const byGim = hotels.find(
      (row) =>
        row.gimmonixHotelId != null && String(row.gimmonixHotelId) === String(gimmonixId),
    );
    if (byGim) return byGim;
  }
  const name = [
    hotelInfo?.HotelName,
    hotelInfo?.DisplayName,
    seed?.name,
    seed?.DisplayName,
    searchPhrase?.hotelName,
    searchPhrase?.DisplayName,
  ]
    .map((value) => String(value || "").trim())
    .find(Boolean);
  if (!name) return null;
  return hotels.find((row) => row.duplicateNamePattern?.test(name)) || null;
}

function quoteCampaignOffers(campaign, searchPhrase, pricingChannel = {}) {
  const arrive =
    isoDateFromDdMmYyyy(searchPhrase?.checkIn) ||
    isoDateFromDdMmYyyy(searchPhrase?.depDate);
  const depart =
    isoDateFromDdMmYyyy(searchPhrase?.checkOut) ||
    isoDateFromDdMmYyyy(searchPhrase?.retDate);
  if (!arrive || !depart) return { offers: [], arrive, depart, occupancies: [] };
  const occupancies = roomsFromHotelSearchPhrase(searchPhrase);
  const pricingOptions = {
    agentChannel: Boolean(pricingChannel.agentChannel),
    publicChannel: Boolean(pricingChannel.publicChannel),
  };
  try {
    const contract = resolveCampaignContract(campaign);
    const offers =
      campaign.quoteKind === "royalTulip"
        ? getCompatibleRoyalTulipRoomOffers(
            occupancies,
            arrive,
            depart,
            contract,
            pricingOptions,
          )
        : getCompatibleRoomOffers(
            occupancies,
            arrive,
            depart,
            contract || belvedereContract,
            pricingOptions,
          );
    return {
      offers: Array.isArray(offers) ? offers : [],
      arrive,
      depart,
      occupancies,
    };
  } catch {
    return { offers: [], arrive, depart, occupancies };
  }
}

function offerRoomCodes(offer) {
  const breakdown = Array.isArray(offer?.roomBreakdown) ? offer.roomBreakdown : [];
  const codes = breakdown
    .map((row) => Number(row?.room?.code ?? row?.roomCode))
    .filter((code) => Number.isFinite(code) && code > 0);
  if (codes.length) return codes;
  const single = Number(offer?.room?.code ?? offer?.roomCode);
  return Number.isFinite(single) && single > 0 ? [single] : [];
}

function resolveCampaignRoomImage(campaign, offer) {
  if (campaign.quoteKind === "royalTulip") {
    return resolveRoyalTulipRoomImageForOffer(offer);
  }
  return resolveBelvedereRoomImageForOffer(offer);
}

export const CAMPAIGN_ALLOTMENT_CANCELLATION_TEXT_HE =
  "ביטול בהתאם למדיניות HOTELSRIVER";

function cancellationText() {
  return CAMPAIGN_ALLOTMENT_CANCELLATION_TEXT_HE;
}

const CAMPAIGN_BOARD_NAME_SUFFIX =
  /\s*[·•\-–]\s*(לינה וארוחת בוקר|חצי פנסיון כשר|חצי פנסיון|הכל כלול|ארוחת בוקר כשרה).*$/u;
const CAMPAIGN_BOARD_ID_SUFFIX = /-(bb|hb|fb|ai)$/i;
const CAMPAIGN_ENGLISH_BOARD_SUFFIX = /\s+(BB|HB|FB|AI)$/i;
const CAMPAIGN_BOARD_RANK = { BB: 0, HB: 1, FB: 2, AI: 3 };

export function stripCampaignBoardFromRoomName(raw, fallback = "Room") {
  const text = String(raw || "").trim();
  if (!text) return fallback;
  const stripped = text
    .replace(CAMPAIGN_BOARD_NAME_SUFFIX, "")
    .replace(CAMPAIGN_ENGLISH_BOARD_SUFFIX, "")
    .trim();
  return stripped || text || fallback;
}

function campaignRoomEnglishRaw(room, extras = {}) {
  return String(
    extras.displayName ||
      extras.roomName ||
      extras.name ||
      room?.name ||
      extras.displayNameHe ||
      extras.roomNameHe ||
      extras.nameHe ||
      room?.nameHe ||
      "",
  ).trim();
}

/** Customer/agent-facing room TYPE name — English contract `name`, board suffix stripped. */
export function campaignRoomTypeDisplayName(offer) {
  const room = offer?.room && typeof offer.room === "object" ? offer.room : offer;
  return stripCampaignBoardFromRoomName(
    campaignRoomEnglishRaw(room, offer),
    "Room",
  );
}

/** @deprecated Display is English; kept as alias for existing callers. */
export function campaignRoomTypeNameHe(offer) {
  return campaignRoomTypeDisplayName(offer);
}

export function campaignOfferDisplayName(offer, fallback = "Room") {
  if (!offer) return fallback;
  const room = offer.room && typeof offer.room === "object" ? offer.room : null;
  return stripCampaignBoardFromRoomName(
    campaignRoomEnglishRaw(room, offer),
    fallback,
  );
}

export function campaignBreakdownRowDisplayName(row) {
  if (!row) return "";
  const room = row.room && typeof row.room === "object" ? row.room : null;
  return String(
    row.roomName ||
      room?.name ||
      row.name ||
      row.roomNameHe ||
      room?.nameHe ||
      row.nameHe ||
      "",
  ).trim();
}

export function resolveCampaignContractRoomByCode(campaignCode, roomCode) {
  const code = Number(roomCode);
  if (!Number.isFinite(code)) return null;
  const hotelCode = String(campaignCode || "").trim();
  const resolved = resolveCampaignContract(hotelCode);
  const contracts = [resolved, royalTulipTbilisiContract, belvedereContract].filter(Boolean);
  const preferred =
    contracts.find((row) => String(row?.hotelCode || "") === hotelCode) || null;
  const search = preferred ? [preferred, ...contracts.filter((row) => row !== preferred)] : contracts;
  for (const contract of search) {
    const match = (contract.roomTypes || []).find((row) => Number(row.code) === code);
    if (match) return match;
  }
  return null;
}

/** Admin / inventory title — official English `name` (keeps BB/HB on Royal Tulip codes). */
export function campaignContractRoomDisplayName(campaignCode, roomCode, fallbackRoom) {
  const match = resolveCampaignContractRoomByCode(campaignCode, roomCode);
  return String(
    match?.name || fallbackRoom?.name || fallbackRoom?.nameEn || fallbackRoom?.nameHe || "",
  ).trim();
}

export function campaignRoomGroupKey(offer) {
  const roomId = String(offer?.room?.id || "").trim();
  const fromId = roomId.replace(CAMPAIGN_BOARD_ID_SUFFIX, "").trim();
  if (fromId) return fromId;
  const imageKey = String(offer?.room?.imageKey || "").trim();
  const occupancy = String(offer?.room?.occupancyLabel || "").trim();
  const typeName = campaignRoomTypeDisplayName(offer);
  return [imageKey, occupancy, typeName].filter(Boolean).join("|") || String(offer?.id || "room");
}

function roomTextBlob(room) {
  return [
    ...(Array.isArray(room?.roomNames) ? room.roomNames : []),
    ...(Array.isArray(room?.bedTypes) ? room.bedTypes : []),
    room?.room?.nameHe,
    room?.room?.name,
    room?.room?.imageKey,
    room?.campaignImageKey,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean)
    .join(" ");
}

export function campaignRoomImageKind(room) {
  const key = String(
    room?.campaignImageKey || room?.room?.imageKey || "",
  )
    .trim()
    .toLowerCase();
  if (key === "family" || key === "standard") return key;
  const text = roomTextBlob(room);
  if (/family|משפחה|connecting/.test(text)) return "family";
  return "standard";
}

function gimmonixRoomImageKindScore(room, kind) {
  const text = roomTextBlob(room);
  if (!text) return 0;
  if (kind === "family") {
    if (/family|משפחה|connecting/.test(text)) return 3;
    return 0;
  }
  if (/family|משפחה|connecting|suite|deluxe|superior/.test(text) && !/standard|סטנדרט/.test(text)) {
    return 0;
  }
  if (/standard|סטנדרט/.test(text)) return 3;
  if (/\btwin\b/.test(text)) return 2;
  if (/\bdouble\b/.test(text)) return 1;
  return 0;
}

function collectRoomImages(room) {
  const lists = [room?.images, room?.roomImages];
  const out = [];
  const seen = new Set();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      const url =
        typeof item === "string"
          ? item
          : item?.url || item?.src || item?.ImageUrl || item?.image;
      if (!url || seen.has(url)) continue;
      seen.add(url);
      out.push(url);
    }
  }
  return out;
}

/**
 * Campaign allotment cards use local gallery fallbacks (e.g. gym photo).
 * When the same hotel profile already has Gimmonix rooms, reuse those photos
 * for Standard / Family types so the kosher special-price card matches the
 * supplier Standard Room.
 */
export function applyGimmonixRoomImagesToCampaignRooms(campaignRooms, gimmonixRooms) {
  const sources = (Array.isArray(gimmonixRooms) ? gimmonixRooms : []).filter(
    (room) => !room?.isCampaignAllotment,
  );
  const bestByKind = new Map();
  for (const room of sources) {
    const images = collectRoomImages(room);
    if (!images.length) continue;
    for (const kind of ["standard", "family"]) {
      const score = gimmonixRoomImageKindScore(room, kind);
      if (score <= 0) continue;
      const current = bestByKind.get(kind);
      if (!current || score > current.score) {
        bestByKind.set(kind, { score, images });
      }
    }
  }
  return (Array.isArray(campaignRooms) ? campaignRooms : []).map((room) => {
    if (!room?.isCampaignAllotment) return room;
    const match = bestByKind.get(campaignRoomImageKind(room));
    if (!match?.images?.length) return room;
    return {
      ...room,
      images: match.images,
      packages: (room.packages || []).map((pkg) => ({
        ...pkg,
        roomImages: match.images,
        room_images: match.images,
      })),
    };
  });
}

function withRoomTypeName(pkg, typeName) {
  return {
    ...pkg,
    roomNames: [typeName],
    Rooms: Array.isArray(pkg?.Rooms)
      ? pkg.Rooms.map((row) => ({ ...row, RoomName: typeName }))
      : pkg?.Rooms,
  };
}

function campaignBoardRank(pkg) {
  const code = String(pkg?.roomBasis || pkg?.RoomBasis || "").toUpperCase();
  return CAMPAIGN_BOARD_RANK[code] ?? 9;
}

export function mapCampaignOfferToHotelProfileRoom(
  campaign,
  offer,
  { occupancies, hotelId, arrive = null, depart = null, publicChannel = false } = {},
) {
  const packageId = `campaign:${campaign.slug}:${offerInventoryKey(offer) || offer?.id || "room"}`;
  const roomName = campaignRoomTypeDisplayName(offer);
  const board = String(offer?.room?.board || offer?.board || "BB").trim();
  const netTotal = Number(offer?.netTotal);
  const sellingTotal = Number(offer?.sellingTotal);
  const currency = normalizeCampaignContractCurrency(campaign.currency);
  const image = resolveCampaignRoomImage(campaign, offer);
  const inventory = offer?.inventory || null;
  const bookable = inventory == null || inventory.bookable !== false;
  const cxl = cancellationText();
  const kosher = Boolean(campaign?.kosher);
  const boardHe =
    String(offer?.room?.boardHe || "").trim() ||
    (kosher ? (board === "HB" ? "חצי פנסיון כשר" : "ארוחת בוקר כשרה") : "");
  const apiNet =
    Number.isFinite(netTotal) && netTotal > 0
      ? netTotal
      : Number.isFinite(sellingTotal) && sellingTotal > 0
        ? sellingTotal
        : null;
  const pkg = {
    packageID: packageId,
    PackageId: packageId,
    packageId,
    isCampaignAllotment: true,
    campaignSlug: campaign.slug,
    campaignQuoteKind: campaign.quoteKind,
    campaignHotelCode: campaign.hotelCode,
    campaignCurrency: currency,
    campaignPublicChannel: Boolean(publicChannel),
    campaignNetTotal: Number.isFinite(netTotal) && netTotal > 0 ? netTotal : null,
    campaignSellingTotal: Number.isFinite(sellingTotal) && sellingTotal > 0 ? sellingTotal : apiNet,
    campaignOffer: offer,
    campaignSelectedRoom: toSelectedRoom(offer),
    campaignOccupancies: occupancies,
    campaignArriveIso: arrive || null,
    campaignDepartIso: depart || null,
    skipInventoryHold: Boolean(campaign.skipInventoryHold),
    skipFinalAvailabilityCheck: true,
    campaignKosher: kosher,
    roomBasis: board,
    RoomBasis: board,
    boardHe,
    boardBasisLabel: boardHe,
    roomNames: [roomName],
    includesTaxes: true,
    refundability: "nonrefundable",
    cancellationPolicy: cxl,
    CancellationPolicy: cxl,
    PackagePrice: {
      FinalPrice: apiNet,
      Currency: currency,
    },
    priceCurrency: currency,
    Currency: currency,
    Rooms: [
      {
        RoomBasis: board,
        roomBasis: board,
        boardHe,
        RoomName: roomName,
        Price: { FinalPrice: apiNet, Currency: currency },
      },
    ],
  };
  return {
    id: packageId,
    hotelID: hotelId ?? null,
    roomNames: [roomName],
    facilities: Array.isArray(offer?.room?.facilities) && offer.room.facilities.length
      ? offer.room.facilities
      : (offer?.room?.roomFacilities || []).map((row) => row?.nameEn || row?.nameHe).filter(Boolean),
    images: image ? [image] : [],
    bedTypes: [],
    SimilarRooms: true,
    isCampaignAllotment: true,
    campaignRoomGroupKey: campaignRoomGroupKey(offer),
    campaignImageKey: String(offer?.room?.imageKey || "").trim() || null,
    campaignKosher: kosher,
    campaignInventoryLabel: inventory?.label_he || null,
    campaignBookable: bookable,
    packages: [pkg],
  };
}

export function mapCampaignOffersToHotelProfileRooms(campaign, offers, extras = {}) {
  const grouped = new Map();
  for (const offer of Array.isArray(offers) ? offers : []) {
    if (offer?.inventory?.bookable === false) continue;
    const mapped = mapCampaignOfferToHotelProfileRoom(campaign, offer, extras);
    const typeName = mapped.roomNames?.[0] || campaignRoomTypeDisplayName(offer);
    const key = mapped.campaignRoomGroupKey || campaignRoomGroupKey(offer);
    const pkg = withRoomTypeName(mapped.packages?.[0], typeName);
    const existing = grouped.get(key);
    if (!existing) {
      grouped.set(key, {
        ...mapped,
        id: `campaign:${campaign.slug}:${key}`,
        roomNames: [typeName],
        packages: pkg ? [pkg] : [],
      });
      continue;
    }
    if (pkg) existing.packages.push(pkg);
    existing.campaignBookable = Boolean(existing.campaignBookable || mapped.campaignBookable);
    if (!existing.images?.length && mapped.images?.length) {
      existing.images = mapped.images;
    }
  }
  return [...grouped.values()].map((room) => ({
    ...room,
    packages: [...(room.packages || [])].sort((a, b) => campaignBoardRank(a) - campaignBoardRank(b)),
  }));
}

function buildCampaignProfileLoadResult(campaign, offers, extras) {
  const rooms = mapCampaignOffersToHotelProfileRooms(campaign, offers, extras);
  const resultPackages = rooms.flatMap((room) =>
    (room.packages || []).map((pkg) => ({
      ...pkg,
      PackageId: pkg.PackageId,
      packageID: pkg.packageID,
    })),
  );
  return {
    rooms,
    resultPackages,
    arrive: extras.arrive || null,
    depart: extras.depart || null,
  };
}

export async function loadCampaignAllotmentRoomsForHotelProfile({
  campaign,
  searchPhrase,
  hotelId,
  agentChannel = false,
  publicChannel = false,
  onQuoted = null,
} = {}) {
  if (!campaign) {
    return { rooms: [], resultPackages: [], arrive: null, depart: null };
  }
  const quoted = quoteCampaignOffers(campaign, searchPhrase, {
    agentChannel,
    publicChannel,
  });
  const extras = {
    occupancies: quoted.occupancies,
    hotelId,
    arrive: quoted.arrive,
    depart: quoted.depart,
    publicChannel: Boolean(publicChannel),
  };
  const initial = buildCampaignProfileLoadResult(campaign, quoted.offers, extras);
  if (typeof onQuoted === "function") onQuoted(initial);
  let offers = quoted.offers;
  if (!campaign.skipInventoryHold && offers.length > 0 && quoted.arrive && quoted.depart) {
    const roomCodes = [...new Set(offers.flatMap(offerRoomCodes))];
    try {
      const availability = await fetchBelvedereCampaignAvailability({
        checkIn: quoted.arrive,
        checkOut: quoted.depart,
        roomCodes,
        campaignCode: campaign.hotelCode,
      });
      const enriched = enrichOffersWithInventory(offers, availability);
      const inventoryByOffer = buildInventoryByOfferMap(enriched);
      offers = applyInventoryGateToOffers(offers, inventoryByOffer, {
        loading: false,
        failed: false,
      });
      return buildCampaignProfileLoadResult(campaign, offers, extras);
    } catch {
      // Inventory API not seeded yet for this campaign — keep contract rooms visible.
    }
  }
  return initial;
}

export function formatCampaignAllotmentPackageDisplay(pkg, profilePriceContext) {
  if (!isCampaignAllotmentPackage(pkg)) return null;
  const currency = normalizeCampaignContractCurrency(
    pkg?.campaignCurrency || pkg?.priceCurrency,
  );
  const markupFactor = resolveCampaignHotelMarkupFactor(pkg);
  const net = Number(pkg?.campaignNetTotal ?? pkg?.campaignOffer?.netTotal);
  let selling = null;
  if (Number.isFinite(net) && net > 0) {
    selling = roundMoney2(net * markupFactor);
  } else {
    const fallback = Number(pkg?.campaignSellingTotal ?? pkg?.PackagePrice?.FinalPrice);
    if (Number.isFinite(fallback) && fallback > 0) selling = roundMoney2(fallback);
  }
  if (selling == null || !Number.isFinite(selling) || selling <= 0) return null;
  const adminFxRate = resolveCampaignAdminFxRate(currency, profilePriceContext);
  const ilsRaw =
    adminFxRate != null ? selling * adminFxRate : null;
  const ilsAmount =
    ilsRaw != null && Number.isFinite(ilsRaw) ? Math.round(ilsRaw) : null;
  const displayLine =
    ilsAmount != null
      ? formatHotelSearchAmountForDisplay(ilsAmount, "ILS")
      : formatHotelContractMoney(selling, currency);
  const rateLabel = formatCampaignAdminFxRateLabel(adminFxRate, currency);
  const sellingLine = formatHotelContractMoney(selling, currency);
  return {
    displayLine,
    chosenDisplayAmount: ilsAmount != null ? ilsAmount : selling,
    chosenDisplayCurrency: ilsAmount != null ? "ILS" : currency,
    sourcePath: "campaign_allotment_net_x_markup_admin_fx",
    rawFinalPrice: Number.isFinite(net) && net > 0 ? net : selling,
    rawOurPriceWithCc: null,
    rejectedSourceReason: null,
    wasConverted: ilsAmount != null,
    sortValue: ilsAmount != null ? ilsAmount : selling,
    rateLabel,
    adminFxRate,
    markupFactor,
    contractCurrency: currency,
    sellingAmount: selling,
    sellingLine,
    skipHotelHrCommission: true,
  };
}
